import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, ScanLine, Globe2, HeartHandshake, Smile, Clock, Award } from "lucide-react";
import api from "@/lib/api";
import { CLINIC } from "@/data/site";
import { trackEvent } from "@/lib/analytics";
import { Seo, LOCAL_BUSINESS_SCHEMA } from "@/components/Seo";
import { Reveal } from "@/components/common/Reveal";
import { SectionHeading } from "@/components/common/SectionHeading";
import { PhilosophyPillars } from "@/components/common/PhilosophyPillars";
import { CdtStandard } from "@/components/common/CdtStandard";
import { ServiceCard } from "@/components/cards/ServiceCard";
import { ArticleCard } from "@/components/cards/ArticleCard";
import { ReviewsSection } from "@/components/sections/ReviewsSection";
import { AppointmentCTA } from "@/components/sections/AppointmentCTA";
import { CallPrompt } from "@/components/sections/CallPrompt";

const IMG = {
  hero: "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=1200&q=80",
  screening: "https://images.unsplash.com/photo-1651008376811-b90baee60c1f?auto=format&fit=crop&w=1200&q=80",
  international: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=1200&q=80",
};

const trust = [
  { Icon: Award, label: "20+ years of experience" },
  { Icon: HeartHandshake, label: "Ethics-first, no over-treatment" },
  { Icon: ScanLine, label: "Digital precision workflows" },
  { Icon: Globe2, label: "International patients welcome" },
];

const benefits = [
  { Icon: Smile, title: "Greater comfort", text: "Digital scans replace messy impressions for a calmer, gentler experience." },
  { Icon: ScanLine, title: "Higher accuracy", text: "Guided planning means precise, predictable, conservative treatment." },
  { Icon: Clock, title: "Fewer visits", text: "Efficient digital workflows often reduce the number of appointments needed." },
  { Icon: ShieldCheck, title: "Predictable outcomes", text: "See and plan your result before treatment begins, with confidence." },
];

export default function Home() {
  const [services, setServices] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/doctors").then((r) => setDoctors(r.data)).catch(() => {});
    api.get("/articles?limit=3").then((r) => setArticles(r.data.items)).catch(() => {});
  }, []);

  return (
    <>
      <Seo title="Thoughtful Dentistry, Greater Noida" jsonLd={LOCAL_BUSINESS_SCHEMA} path="/" />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10" style={{ background: "radial-gradient(1200px 600px at 20% 10%, rgba(15,92,90,0.14), transparent 60%), radial-gradient(900px 500px at 85% 0%, rgba(200,168,107,0.12), transparent 55%)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 lg:pt-24 lg:pb-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.span initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 rounded-full bg-white border border-[color:var(--cdt-border)] px-3 py-1 text-xs text-[var(--cdt-muted)]">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--cdt-accent)]" /> Ethical · Precise · Long-term care
              </motion.span>
              <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }} className="font-display text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.05] tracking-[-0.02em] text-[var(--cdt-text)] mt-5">
                Thoughtful Dentistry for <span className="text-[var(--cdt-primary)]">Lifelong Oral Health</span>
              </motion.h1>
              <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }} className="mt-6 text-lg leading-8 text-[var(--cdt-muted)] max-w-xl">
                At the Center Of Dental Treatment in {CLINIC.city}, we take the time to listen, diagnose carefully, and explain every option honestly — combining digital precision with genuinely patient-first care.
              </motion.p>
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.18 }} className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/appointment" onClick={() => trackEvent("appointment_cta_click", { location: "hero" })} data-testid="hero-appointment-button" className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-[var(--cdt-radius-sm)] bg-[var(--cdt-primary)] text-white font-semibold hover:bg-[var(--cdt-primary-2)] transition-colors">
                  Request Appointment <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/about" data-testid="hero-philosophy-button" className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-[var(--cdt-radius-sm)] bg-white border border-[color:var(--cdt-border)] text-[var(--cdt-text)] font-semibold hover:bg-[var(--cdt-surface-2)] transition-colors">
                  Our Philosophy
                </Link>
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }} className="relative">
              <div className="rounded-[var(--cdt-radius-lg)] overflow-hidden shadow-[var(--cdt-shadow-lg)]">
                <img src={IMG.hero} alt="Calm dental consultation at CDT" className="w-full h-[360px] lg:h-[480px] object-cover" />
              </div>
              <div className="absolute -bottom-5 -left-3 sm:left-6 bg-white rounded-[var(--cdt-radius-md)] shadow-[var(--cdt-shadow-md)] border border-[color:var(--cdt-border)] px-5 py-4 max-w-[220px]">
                <p className="font-display text-2xl text-[var(--cdt-primary)]">4.9★</p>
                <p className="text-xs text-[var(--cdt-muted)] mt-1">Rated by 180+ patients on Google</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-[color:var(--cdt-border)] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {trust.map((t) => (
              <div key={t.label} className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-full bg-[rgba(15,92,90,0.08)] flex items-center justify-center flex-shrink-0"><t.Icon className="h-4.5 w-4.5 h-5 w-5 text-[var(--cdt-primary)]" /></span>
                <span className="text-sm text-[var(--cdt-text)]">{t.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy pillars */}
      <section className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Our Philosophy" title="Dentistry, done thoughtfully" subtitle="Three commitments guide everything we do — and you'll feel them at every visit." />
          <div className="mt-12"><PhilosophyPillars /></div>
        </div>
      </section>

      {/* CDT Standard */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="The CDT Standard" title="A care pathway you can trust" subtitle="Every patient journey follows the same principled five-step standard." />
          <div className="mt-12"><CdtStandard /></div>
        </div>
      </section>

      {/* Digital dentistry benefits-first */}
      <section className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Digital Dentistry" title="Precision you can feel — benefits first" subtitle="Technology matters only because of what it does for you: more comfort, more accuracy, fewer visits." />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => (
              <Reveal key={b.title} delay={(i % 4) * 0.06}>
                <div className="h-full rounded-[var(--cdt-radius-md)] bg-white border border-[color:var(--cdt-border)] shadow-[var(--cdt-shadow-sm)] p-6">
                  <div className="h-11 w-11 rounded-full bg-[rgba(200,168,107,0.14)] flex items-center justify-center"><b.Icon className="h-5 w-5 text-[var(--cdt-accent)]" /></div>
                  <h3 className="font-display text-lg mt-4 text-[var(--cdt-text)]">{b.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-[var(--cdt-muted)]">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/digital-dentistry" data-testid="home-digital-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--cdt-primary)]">Explore digital dentistry <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      {/* Oral Cancer Centre primary highlight */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="grid lg:grid-cols-2 gap-10 items-center rounded-[var(--cdt-radius-lg)] border border-[color:var(--cdt-border)] overflow-hidden bg-[var(--cdt-surface-2)]">
              <div className="p-8 lg:p-12">
                <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-[var(--cdt-accent)]">A Dedicated Centre</span>
                <h2 className="font-display text-3xl md:text-4xl mt-3 leading-tight text-[var(--cdt-text)]">Oral Cancer &amp; Precancer Centre</h2>
                <p className="mt-4 text-[var(--cdt-muted)] leading-8">Led by specialist Dr Ritika Sharma, our centre offers calm, sensitive screening and education for oral cancer and potentially malignant disorders. Early detection can be life-saving.</p>
                <Link to="/oral-cancer-centre" data-testid="home-oral-cancer-link" className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-[var(--cdt-radius-sm)] bg-[var(--cdt-primary)] text-white font-semibold hover:bg-[var(--cdt-primary-2)] transition-colors">Learn about screening <ArrowRight className="h-4 w-4" /></Link>
              </div>
              <div className="h-64 lg:h-full"><img src={IMG.screening} alt="Oral cancer screening" loading="lazy" className="w-full h-full object-cover" /></div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Services preview */}
      <section className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Our Services" title="Comprehensive, precision-led care" subtitle="From preventive check-ups to full mouth rehabilitation — planned around you." />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {services.slice(0, 6).map((s, i) => <ServiceCard key={s.slug} service={s} index={i} />)}
          </div>
          <div className="mt-10 text-center">
            <Link to="/services" data-testid="home-services-link" className="inline-flex items-center gap-2 h-11 px-5 rounded-[var(--cdt-radius-sm)] bg-white border border-[color:var(--cdt-border)] font-semibold text-[var(--cdt-text)] hover:bg-[var(--cdt-surface-2)] transition-colors">View all services <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      {/* International patients teaser */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div className="h-64 lg:h-80 rounded-[var(--cdt-radius-lg)] overflow-hidden shadow-[var(--cdt-shadow-md)] order-2 lg:order-1"><img src={IMG.international} alt="International patient travel" loading="lazy" className="w-full h-full object-cover" /></div>
              <div className="order-1 lg:order-2">
                <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-[var(--cdt-accent)]">For NRIs &amp; International Patients</span>
                <h2 className="font-display text-3xl md:text-4xl mt-3 leading-tight text-[var(--cdt-text)]">Care that travels with you</h2>
                <p className="mt-4 text-[var(--cdt-muted)] leading-8">Share your reports for a remote consultation, receive a transparent treatment plan and timeline, and let us guide your visit — with fewer appointments thanks to our digital workflows.</p>
                <Link to="/international-patients" data-testid="home-international-link" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[var(--cdt-primary)]">Plan your visit <ArrowRight className="h-4 w-4" /></Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Doctors preview */}
      <section className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Meet Your Dentists" title="Experienced hands, ethical hearts" subtitle="Two dedicated clinicians with over two decades of experience each." />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {doctors.map((d, i) => (
              <Reveal key={d.slug} delay={i * 0.08}>
                <Link to={`/doctors/${d.slug}`} data-testid={`home-doctor-${d.slug}`} className="group flex gap-5 h-full rounded-[var(--cdt-radius-md)] bg-white border border-[color:var(--cdt-border)] shadow-[var(--cdt-shadow-sm)] hover:shadow-[var(--cdt-shadow-md)] transition-shadow p-5">
                  <img src={d.image} alt={d.name} loading="lazy" className="h-28 w-24 rounded-[var(--cdt-radius-sm)] object-cover flex-shrink-0" />
                  <div>
                    <h3 className="font-display text-xl text-[var(--cdt-text)]">{d.name}</h3>
                    <p className="text-sm text-[var(--cdt-primary)] font-medium">{d.title}</p>
                    <p className="mt-2 text-xs text-[var(--cdt-muted)]">{d.qualifications.join(", ")} · {d.experience}</p>
                    <p className="mt-2 text-sm leading-6 text-[var(--cdt-muted)] line-clamp-2">{d.philosophy}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Knowledge preview */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Knowledge Center" title="Learn before you decide" subtitle="Clear, honest education to help you make confident choices about your care." />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {articles.map((a, i) => <ArticleCard key={a.slug} article={a} index={i} />)}
          </div>
          <div className="mt-10 text-center">
            <Link to="/knowledge" data-testid="home-knowledge-link" className="inline-flex items-center gap-2 h-11 px-5 rounded-[var(--cdt-radius-sm)] bg-white border border-[color:var(--cdt-border)] font-semibold text-[var(--cdt-text)] hover:bg-[var(--cdt-surface-2)] transition-colors">Visit the Knowledge Center <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      <ReviewsSection />
      <CallPrompt location="home" />
      <AppointmentCTA />
    </>
  );
}
